import React from "react";
import { ShieldCheck, Mail, Phone, Users, CheckCircle, MapPin } from "lucide-react";

export default function About() {
  return (
    <div className="bg-white" data-testid="about-page">
      
      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center space-y-4 max-w-3xl">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">About Our Agency</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            U.S. Based Lead Generation & Digital Marketing Specialists
          </h1>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Delivering high-quality, pre-screened homeowner inquiries with complete corporate transparency and professional compliance.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left text column */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">Established B2B Partner</span>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                Empowering Contractors Through Quality & Integrity
              </h2>
              
              <p className="text-base text-slate-600 leading-relaxed">
                <strong>Precision Home Leads LLC</strong> is a leading U.S.-based lead generation and digital marketing firm. Our core business model focuses on generating qualified homeowner inquiries through compliant online advertising and immediately connecting those homeowners with licensed local contractors across the United States.
              </p>

              <p className="text-sm text-slate-500 leading-relaxed">
                Unlike mass-distribution lead brokers who oversell the same homeowner request to dozens of bidders, we build tailored, high-intent campaigns. Our business model is centered on delivery speed, pre-qualification, and exclusive routing, enabling contractors to spend less time bidding and more time performing quality work.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-900 text-sm">Professional Support</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Our support team operates standard corporate business hours and reviews claims on a case-by-case basis according to our clear refund policies.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-900 text-sm">Corporate Integrity</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    No false claims, fake reviews, or exaggerated income numbers. We focus strictly on measurable lead delivery and commercial growth metrics.
                  </p>
                </div>
              </div>
            </div>

            {/* Right image column */}
            <div className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border border-slate-200 shadow-md">
                <img 
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4Mjc4NjE5MHww&ixlib=rb-4.1.0&q=85" 
                  alt="Team Collaboration" 
                  className="w-full h-auto object-cover object-center max-h-[450px]"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Compliance pillars */}
      <section className="bg-slate-50 border-y border-slate-100 py-16 md:py-20" data-testid="compliance-pillars-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-4 mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">Risk Assessment & Verification</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Our Core Compliance Standards
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
              Precision Home Leads LLC is fully aligned with FTC guidelines, TCPA text marketing frameworks, and credit card processor risk validation standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Pillar 1 */}
            <div className="bg-white p-8 rounded border border-slate-200/80 shadow-sm space-y-4">
              <div className="w-10 h-10 rounded bg-slate-900 text-white flex items-center justify-center font-bold text-sm">01</div>
              <h3 className="font-semibold text-slate-900 text-base">Complete Transparency</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Clear descriptions of service delivery mechanisms, terms, and billing procedures. We provide direct access to contracts and transaction logs with zero hidden clauses.
              </p>
            </div>

            {/* Pillar 2 */}
            <div className="bg-white p-8 rounded border border-slate-200/80 shadow-sm space-y-4">
              <div className="w-10 h-10 rounded bg-slate-900 text-white flex items-center justify-center font-bold text-sm">02</div>
              <h3 className="font-semibold text-slate-900 text-base">Verified Lead Verification</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                We confirm project scopes, home ownership, and contact availability. Leads that fail to meet these pre-defined standards are reviewed and swapped case-by-case.
              </p>
            </div>

            {/* Pillar 3 */}
            <div className="bg-white p-8 rounded border border-slate-200/80 shadow-sm space-y-4">
              <div className="w-10 h-10 rounded bg-slate-900 text-white flex items-center justify-center font-bold text-sm">03</div>
              <h3 className="font-semibold text-slate-900 text-base">Secure Underwriting Safeguards</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                SSL certification, strict customer privacy notices, and full alignment with major card brand merchant underwriting standards. No fake testimonials or false guarantees.
              </p>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
}
